import { mdiAlertCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { AiTargetType } from '../../api';
import { humanSeconds, OPERATIONS } from '../../services/ai/ai.derive';
import { AiStore } from '../../stores/ai/ai.store';
import { BaseButton } from '../base/button/button';
import { TargetTag } from './ai-fields';
import * as S from './ai.styles';

const PROFILE_COLUMNS = 'minmax(160px,1.3fr) 84px minmax(200px,1.6fr) minmax(120px,1fr) 100px minmax(130px,1fr)';

/**
 * The provider app's view of what it may use: the enabled profiles `GET /provider/ai` offers this account, read-only.
 *
 * It is not the support app's Configuration tab and must not look like one. A provider cannot see account limits,
 * disabled profiles or the agent catalog, so none of them appear: absent data is not shown as an empty value (NS-4).
 */
export const ProfilesTab = observer(({ store }: { store: AiStore }): React.ReactElement => {
    const { access } = store;

    if (store.loadState === 'demo') {
        // NS-14: nothing is requested in demo mode, and nothing real may render.
        return (
            <S.Centered>
                <S.CenteredTitle>Unavailable in demo mode</S.CenteredTitle>
                The AI profiles belong to a real account, so they are not shown while demo mode is on.
            </S.Centered>
        );
    }
    // Two ways to fail: the first load (`loadState` 'error'), or a later refresh, such as the one opening Issue key
    // makes. A failed refresh clears `access` and records `accessError` without touching `loadState`, so it needs its own
    // check, or the tab would sit on "Loading profiles…" forever with nothing to click.
    const failure = store.loadState === 'error' ? store.loadError : !access ? store.accessError : '';
    if (failure) {
        // NS-3: a failed request is "unknown", never "AI is not enabled".
        return (
            <S.Centered>
                <Icon path={mdiAlertCircle} size={1} />
                <S.CenteredTitle>The AI profiles could not be loaded</S.CenteredTitle>
                {failure} — whether AI is enabled for this account is unknown.
                <BaseButton variant="outlined" onClick={(): void => void store.load()}>
                    Retry
                </BaseButton>
            </S.Centered>
        );
    }
    if (!access) {
        return <S.Centered>Loading profiles…</S.Centered>;
    }
    if (!access.enabled || access.profiles.length === 0) {
        return (
            <S.Centered>
                <S.CenteredTitle>No AI profiles available</S.CenteredTitle>
                AI is not enabled for this account, or no profile is available to it right now. Contact Enter support to
                set it up.
            </S.Centered>
        );
    }

    return (
        <S.Stack>
            <S.Table>
                <S.TableHead>
                    <S.Grow>
                        <S.CardTitle>Available profiles</S.CardTitle>
                        <S.CardSub>
                            What this account may use. Choose one when you issue a key; a key keeps the policy it was issued
                            with. Enter support manages these.
                        </S.CardSub>
                    </S.Grow>
                </S.TableHead>
                <S.Scroll>
                    <S.Row header={true} columns={PROFILE_COLUMNS} min={880}>
                        <span>Profile</span>
                        <span>Type</span>
                        <span>Target</span>
                        <span>Operations</span>
                        <span>TTL def / max</span>
                        <span>Key limits</span>
                    </S.Row>
                    {access.profiles.map(p => {
                        const isAgent = p.targetType === AiTargetType.AGENT;
                        const kl = p.keyLimits;
                        return (
                            <S.Row key={p.id} columns={PROFILE_COLUMNS} min={880}>
                                <S.Cell>
                                    <S.Strong>{p.displayName}</S.Strong>
                                    <S.Mono>{p.id}</S.Mono>
                                    {p.description && <S.Muted>{p.description}</S.Muted>}
                                </S.Cell>
                                <span>
                                    <TargetTag type={p.targetType} />
                                </span>
                                <S.Cell>
                                    {isAgent ? (
                                        <>
                                            <S.Mono>{p.agentId ?? '—'}</S.Mono>
                                            {/* The agent's name and model arrive with an issued key, not with this list. */}
                                            <S.Muted>Agent · its name and model are shown when a key is issued</S.Muted>
                                        </>
                                    ) : (
                                        <>
                                            <S.Mono>{(p.models ?? []).join(', ') || '—'}</S.Mono>
                                            <S.Muted>{p.defaultModel ? `Default: ${p.defaultModel}` : 'No default'}</S.Muted>
                                        </>
                                    )}
                                </S.Cell>
                                <span>
                                    {(isAgent ? ['CHAT_COMPLETIONS'] : p.operations)
                                        .map(o => OPERATIONS.find(x => x.value === o)?.label ?? o)
                                        .join(', ')}
                                </span>
                                <span>
                                    {humanSeconds(p.defaultLifetimeSeconds)} / {humanSeconds(p.maxLifetimeSeconds)}
                                </span>
                                <span>
                                    {kl ? (
                                        <>
                                            ${kl.maxBudgetUsd}
                                            {kl.requestsPerMinute ? ` · ${kl.requestsPerMinute} rpm` : ''}
                                            {kl.tokensPerMinute ? ` · ${kl.tokensPerMinute / 1000}k tpm` : ''}
                                        </>
                                    ) : (
                                        '—'
                                    )}
                                </span>
                            </S.Row>
                        );
                    })}
                </S.Scroll>
            </S.Table>
        </S.Stack>
    );
});
